import axios from 'axios';
import endpoints from 'constants/endpoints';
import { stateAbbreviations } from 'constants/enum';
import { safeAWSCallPost } from 'helpers/firebaseSetup';
import { getCompany } from 'helpers/getCompanyVariables';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

const company = getCompany();
const INITIAL_VALUES = {
  agents: [],
  agencies: [],
  carriers: [],
  states: [],
  initial_date: getDefaultDate({
    type: 'initial_date'
  }),
  end_date: getDefaultDate({
    type: 'end_date'
  }),
  date_type: company === 'sunshine' ? 'Statement Month' : 'Coverage Month',
  agentStatus: ['Active'],
  commission_type: 'All'
};

function getDefaultDate({
  startDate = new Date(new Date().setMonth(new Date().getMonth() - 4)),
  type
}) {
  if (!(startDate instanceof Date) || isNaN(startDate)) {
    console.error('Invalid Date object provided.');
    return;
  }

  const currentDate = new Date();
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

  const month = String(
    (type === 'initial_date' ? start.getMonth() : end.getMonth()) + 1
  ).padStart(2, '0'); // Months are 0-indexed

  const year = (type === 'initial_date' ? start : end).getFullYear();

  return `${month}-${year}`;
}

function getMonthsInBetween({ startDate, endDate }) {
  if (!(startDate instanceof Date) || isNaN(startDate)) {
    console.error('Invalid Date object provided.');
    return;
  }

  const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

  let months = [];

  while (start < end) {
    const month = String(start.getMonth() + 1);
    const year = start.getFullYear();
    // Increment by one month
    start.setMonth(start.getMonth() + 1);
    months.push(`${month}/${year}`);
  }

  return months;
}

const useAgentsCommissions = ({
  setLocalLoader,
  setFilterCount,
  setShowFilters
}) => {
  const cancelTokenSource = axios.CancelToken.source();

  const [data_for_graph, setDataForGraph] = useState([]);
  const [data_for_table, setDataForTable] = useState([]);
  const [data_for_table_member, setDataForTableMember] = useState([]);
  const [data_for_state_commissions, setDataForStateCommissions] = useState({});

  const [months, setMonths] = useState([]);

  const [formData, setformData] = useState(INITIAL_VALUES);

  const { satementsCarriersArray } = useSelector(state => state.appointments);

  const carriersOptions = useMemo(() => {
    return satementsCarriersArray
      .map(option => {
        return {
          type: option.type,
          name: option.name,
          id: option.AgencyBloc_CarrierID,
          Callidus_Carrier_Names: option.Callidus_Carrier_Names
        };
      })
      .concat([
        {
          type: 'Marketplace',
          name: 'Other',
          id: '',
          Callidus_Carrier_Names: 'Other'
        }
      ])
      .filter(carrier => carrier.type);
  }, [satementsCarriersArray]);

  const carrierCommissionsColors = useMemo(() => {
    return (satementsCarriersArray || []).reduce((acc, carrier) => {
      if (!carrier?.name) return acc;
      acc[carrier.name] = carrier.commissionsColor;
      return acc;
    }, {});
  }, [satementsCarriersArray]);

  const getAgentsCommissionsData = useCallback(filters => {
    setLocalLoader(true);

    const isStatementDates =
      filters.date_type === 'Statement Date' ||
      filters.date_type === 'Statement Month';

    safeAWSCallPost(
      endpoints.aws.CustomersCommissions,
      {
        filters: JSON.stringify({
          carriers: filters.carriers,
          states: filters.states.map(state => stateAbbreviations[state]),
          start_date: `${filters.initial_date.split('-')[1]}-${
            filters.initial_date.split('-')[0]
          }-01`,
          end_date: `${filters.end_date.split('-')[1]}-${
            filters.end_date.split('-')[0]
          }-${new Date(
            filters.end_date.split('-')[1],
            filters.end_date.split('-')[0],
            0
          ).getDate()}`,
          isStatementDates,
          agentStatus: filters.agentStatus,
          commission_type: filters.commission_type
        }),
        agents: filters.agents.map(agent => agent.agentId),
        agencies: filters.agencies.map(agency => agency.id)
      },
      false,
      cancelTokenSource.token
    )
      .then(response => {
        let _months = [];

        if (filters.date_type === 'Statement Date') {
          _months = [
            ...new Set(
              response.data.records.map(record => record.statementdate)
            )
          ].sort((a, b) => new Date(a) - new Date(b));
        } else {
          _months = getMonthsInBetween({
            startDate: new Date(
              parseInt(filters.initial_date.split('-')[1]),
              parseInt(filters.initial_date.split('-')[0]) - 1,
              1
            ),
            endDate: new Date(
              parseInt(filters.end_date.split('-')[1]),
              parseInt(filters.end_date.split('-')[0]),
              1
            )
          });
        }

        const commissionsForGraph = [];

        const commissionsTableByCarrier = {};

        const commissionsTableByCarrierMembers = {};

        const commissionsByState = {};

        for (const month of _months) {
          const findCommissions = response.data.records.filter(
            record =>
              (filters.date_type === 'Statement Date'
                ? record.statementdate
                : filters.date_type === 'Statement Month'
                ? `${record.statementdate.split('/')[0].replace(/^0/, '')}/${
                    record.statementdate.split('/')[2]
                  }`
                : record.coverage_month) === month
          );

          const commissionMap = {
            name: month,
            Total: 0
          };

          for (const commission of findCommissions || [[]]) {
            if (!commissionsByState[commission.state]) {
              commissionsByState[commission.state] = {};
            }

            if (
              !commissionsByState[commission.state][commission.carrier_name]
            ) {
              commissionsByState[commission.state][commission.carrier_name] = {
                commission: Math.trunc(commission.total_payable),
                membercount: Math.trunc(commission.membercount || 0)
              };
            } else {
              commissionsByState[commission.state][
                commission.carrier_name
              ].commission += Math.trunc(commission.total_payable);
              commissionsByState[commission.state][
                commission.carrier_name
              ].membercount += Math.trunc(commission.membercount || 0);
            }

            if (!commissionMap[commission.carrier_name]) {
              commissionMap[commission.carrier_name] = Math.trunc(
                commission.total_payable
              );
            } else {
              commissionMap[commission.carrier_name] += Math.trunc(
                commission.total_payable
              );
            }
            commissionMap.Total += Math.trunc(commission.total_payable);

            if (!commissionsTableByCarrier[commission.carrier_name]) {
              commissionsTableByCarrier[commission.carrier_name] = {};
              commissionsTableByCarrierMembers[commission.carrier_name] = {
                Total: 0
              };
            }

            if (!commissionsTableByCarrier[commission.carrier_name][month]) {
              commissionsTableByCarrier[commission.carrier_name][month] =
                Math.trunc(commission.total_payable);

              commissionsTableByCarrierMembers[commission.carrier_name][month] =
                Math.trunc(commission.membercount);
            } else {
              commissionsTableByCarrier[commission.carrier_name][month] +=
                Math.trunc(commission.total_payable);

              commissionsTableByCarrierMembers[commission.carrier_name][
                month
              ] += Math.trunc(commission.membercount);
            }

            commissionsTableByCarrierMembers[commission.carrier_name].Total +=
              Math.trunc(commission.membercount);
          }
          commissionsForGraph.push(commissionMap);
        }

        const dataForTable = Object.keys(commissionsTableByCarrier)
          .sort()
          .map(key => ({
            carriername: key,
            ...commissionsTableByCarrier[key]
          }));

        const dataForTableMember = Object.keys(commissionsTableByCarrierMembers)
          .sort()
          .map(key => ({
            carriername: key,
            ...commissionsTableByCarrierMembers[key]
          }));

        setLocalLoader(false);
        setDataForTable(dataForTable);
        setDataForTableMember(dataForTableMember);
        setMonths(_months);
        setDataForGraph(commissionsForGraph);
        setDataForStateCommissions(commissionsByState);
      })
      .catch(() => {
        setLocalLoader(false);
        setDataForGraph([]);
        setDataForTable([]);
        setMonths([]);
        setDataForStateCommissions({});
      });
  }, []);

  const clearFilters = useCallback(() => {
    const clearedFormData = {
      ...INITIAL_VALUES,
      agents: [...formData.agents]
    };
    // Calculate filter count: agentStatus always has 'Active' by default, so count is 1
    setFilterCount(1);
    setformData(() => clearedFormData);
  }, [setFilterCount, setformData, formData.agents]);

  const applyFilters = useCallback(() => {
    if (formData.agents.length === 0 && formData.agencies.length === 0) return;
    getAgentsCommissionsData(formData);
    setShowFilters(false);
  }, [getAgentsCommissionsData, formData]);

  useEffect(() => {
    applyFilters();
  }, [formData.date_type, formData.commission_type]);

  const handleChangeSelectValue = useCallback(
    (_values, property) => {
      let values = [];

      if (property === 'date_type' || property === 'commission_type') {
        setformData(prevState => ({
          ...prevState,
          [property]: _values
        }));
        return;
      }

      if (typeof _values === 'string') {
        values = formData[property][0] === _values ? [] : [_values];
      } else {
        values = _values;
      }

      if (property === 'initial_date' || property === 'end_date') {
        setformData(prevState => ({
          ...prevState,
          [property]: values[0]
        }));
        return;
      }

      if (
        formData[property].length === 0 &&
        values.length > 0 &&
        property !== 'agents' &&
        property !== 'agencies'
      ) {
        setFilterCount(count => (count += 1));
      } else if (
        formData[property].length > 0 &&
        values.length === 0 &&
        property !== 'agents' &&
        property !== 'agencies'
      ) {
        setFilterCount(count => (count -= 1));
      }

      setformData(prevState => ({
        ...prevState,
        [property]: values
      }));
    },
    [setformData, formData, setFilterCount]
  );

  return {
    data_for_graph,
    data_for_table,
    formData,
    setformData,
    clearFilters,
    applyFilters,
    handleChangeSelectValue,
    carriersOptions,
    carrierCommissionsColors,
    months,
    data_for_table_member,
    data_for_state_commissions
  };
};

useAgentsCommissions.propTypes = {
  setLocalLoader: PropTypes.func,
  setFilterCount: PropTypes.func,
  setShowFilters: PropTypes.func
};

export default useAgentsCommissions;
